import React, { useState } from 'react';
import { Box, Typography, Paper, Button, Divider, Chip, Alert } from '@mui/material';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import { useAuth } from '../contexts/AuthContext';
import { useThemeMode } from '../contexts/ThemeContext';
import TimezoneSetupModal from '../components/TimezoneSetupModal';
import { browserTimezone, formatDateTime } from '../utils/datetime';

const ROLE_LABEL: Record<string, string> = {
  super_admin: 'Super Admin',
  admin:       'Admin',
  viewer:      'Viewer',
  app_viewer:  'App Viewer',
};

const ProfilePage: React.FC = () => {
  const { user } = useAuth();
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  const [tzOpen, setTzOpen] = useState(false);

  const textP = isDark ? '#EFEFEF' : '#0A0A0A';
  const textS = isDark ? '#888888' : '#666666';
  const border = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)';
  const card = isDark ? '#111111' : '#FFFFFF';

  const detected = browserTimezone();
  const now = new Date().toISOString();

  if (!user) return null;

  const usingBrowserFallback = !user.timezone;

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto', p: 3 }}>
      <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, letterSpacing: '-0.02em', mb: 0.5 }}>
        Profile
      </Typography>
      <Typography sx={{ fontSize: '0.85rem', color: textS, mb: 3 }}>
        Your account details and dashboard preferences.
      </Typography>

      {/* Account block */}
      <Paper sx={{ p: 3, borderRadius: 2, border: `1px solid ${border}`, bgcolor: card, mb: 3, boxShadow: 'none' }}>
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: textS, letterSpacing: '0.08em', textTransform: 'uppercase', mb: 2 }}>
          Account
        </Typography>
        <Row label="Name" value={user.name} textP={textP} textS={textS} />
        <Row label="Email" value={user.email} textP={textP} textS={textS} />
        <Row
          label="Role"
          value={<Chip size="small" label={ROLE_LABEL[user.role] || user.role} sx={{ fontWeight: 600 }} />}
          textP={textP}
          textS={textS}
        />
        <Row
          label="Last login"
          value={user.last_login ? formatDateTime(user.last_login, user.timezone) : '—'}
          textP={textP}
          textS={textS}
        />
      </Paper>

      {/* Preferences block */}
      <Paper sx={{ p: 3, borderRadius: 2, border: `1px solid ${border}`, bgcolor: card, boxShadow: 'none' }}>
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: textS, letterSpacing: '0.08em', textTransform: 'uppercase', mb: 2 }}>
          Preferences
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, py: 1 }}>
          <PublicOutlinedIcon sx={{ mt: 0.5, color: textS }} />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: textP }}>
              Timezone
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', color: textS, mt: 0.25 }}>
              {user.timezone || `${detected} (browser default)`}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: textS, mt: 1, fontFamily: 'monospace' }}>
              Now: {formatDateTime(now, user.timezone)}
            </Typography>
          </Box>
          <Button variant="outlined" size="small" onClick={() => setTzOpen(true)}>
            Change
          </Button>
        </Box>

        {usingBrowserFallback && (
          <Alert severity="info" sx={{ mt: 2, fontSize: '0.8rem' }}>
            You haven't picked a timezone yet — timestamps are shown in your browser's default zone.
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography sx={{ fontSize: '0.72rem', color: textS }}>
          Note: analytics charts (hourly & daily) are always bucketed in UTC. Raw timestamps (logs, last-login, requests) use your timezone.
        </Typography>
      </Paper>

      <TimezoneSetupModal open={tzOpen} onClose={() => setTzOpen(false)} />
    </Box>
  );
};

const Row: React.FC<{ label: string; value: React.ReactNode; textP: string; textS: string }> = ({ label, value, textP, textS }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, alignItems: 'center' }}>
    <Typography sx={{ fontSize: '0.8rem', color: textS, fontWeight: 500 }}>{label}</Typography>
    <Box sx={{ fontSize: '0.9rem', color: textP, fontWeight: 600, textAlign: 'right' }}>{value}</Box>
  </Box>
);

export default ProfilePage;
